import { Component, EventEmitter, Input, Output, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { MessageService } from 'primeng/api';
import { TicketingService } from '../../../services/ticketing.service';
import { NGXLogger } from 'ngx-logger';
import { AuthService, User } from '@auth0/auth0-angular';

@Component({
  selector: 'app-support-ticket-dialog',
  templateUrl: './support-ticket-dialog.component.html',
  styleUrl: './support-ticket-dialog.component.scss'
})
export class SupportTicketDialogComponent implements OnInit, OnChanges {
  @Input() visible = false;
  @Output() visibleChange = new EventEmitter<boolean>();

  customerName = '';
  customerEmail = '';
  subject = '';
  message = '';
  attachments: File[] = [];
  sendingTicket = false;
  maxFileSize = 10 * 1024 * 1024; // 10MB
  private currentUser: User | null | undefined = null;

  constructor(
    private ticketingService: TicketingService,
    private messageService: MessageService,
    private logger: NGXLogger,
    private auth: AuthService
  ) {}

  ngOnInit(): void {
    // Sottoscrivi ai dati dell'utente loggato
    this.auth.user$.subscribe(user => {
      this.currentUser = user;
      this.initializeUserData();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Quando il dialog diventa visibile, inizializza i dati utente
    if (changes['visible'] && this.visible && !changes['visible'].firstChange) {
      this.initializeUserData();
    }
  }

  private initializeUserData(): void {
    if (this.currentUser) {
      // Pre-compila nome ed email con i dati dell'utente loggato
      if (!this.customerName && this.currentUser.name) {
        this.customerName = this.currentUser.name;
      }
      if (!this.customerEmail && this.currentUser.email) {
        this.customerEmail = this.currentUser.email;
      }
    }
  }

  close(): void {
    this.visible = false;
    this.visibleChange.emit(false);
    this.resetForm();
    document.body.style.overflow = '';
  }

  private resetForm(): void {
    // Mantieni nome ed email se l'utente è loggato
    if (!this.currentUser) {
      this.customerName = '';
      this.customerEmail = '';
    }
    this.subject = '';
    this.message = '';
    this.attachments = [];
    this.sendingTicket = false;
  }

  onFileSelect(event: any): void {
    if (event.files && event.files.length > 0) {
      const newFiles: File[] = Array.from(event.files);

      // Verifica dimensione file
      for (const file of newFiles) {
        if (file.size > this.maxFileSize) {
          this.messageService.add({
            severity: 'warn',
            summary: 'File troppo grande',
            detail: `Il file ${file.name} supera la dimensione massima di 10MB`,
            life: 5000
          });
          return;
        }
      }

      this.attachments = [...this.attachments, ...newFiles];
      this.logger.info('[Support Ticket Dialog] File aggiunti:', this.attachments.map(f => f.name));
    }
  }

  removeAttachment(index: number): void {
    this.attachments.splice(index, 1);
  }

  getFileIcon(filename: string): string {
    const ext = filename.split('.').pop()?.toLowerCase();
    switch (ext) {
      case 'pdf': return 'picture_as_pdf';
      case 'doc':
      case 'docx': return 'description';
      case 'xls':
      case 'xlsx': return 'table_chart';
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif': return 'image';
      case 'zip':
      case 'rar': return 'folder_zip';
      default: return 'attach_file';
    }
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  }

  isFormValid(): boolean {
    return !!(
      this.customerName.trim() &&
      this.customerEmail.trim() &&
      this.isValidEmail(this.customerEmail) &&
      this.subject.trim() &&
      this.message.trim()
    );
  }

  private isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  send(): void {
    if (!this.isFormValid()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validazione',
        detail: 'Compila tutti i campi obbligatori con valori validi',
        life: 4000
      });
      return;
    }

    this.sendingTicket = true;

    this.ticketingService.createTicket(
      this.customerName.trim(),
      this.customerEmail.trim(),
      this.subject.trim(),
      this.message.trim(),
      this.attachments.length > 0 ? this.attachments : undefined
    ).subscribe({
      next: (response) => {
        this.logger.info('[Support Ticket Dialog] Ticket creato con successo:', response);
        this.messageService.add({
          severity: 'success',
          summary: 'Ticket Creato',
          detail: `Ti contatteremo al più presto!`,
          life: 5000
        });
        this.close();
      },
      error: (error) => {
        this.logger.error('[Support Ticket Dialog] Errore durante la creazione del ticket:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: error?.error?.message || 'Errore durante la creazione del ticket. Riprova più tardi.',
          life: 5000
        });
        this.sendingTicket = false;
      }
    });
  }
}
